import Axios from 'axios';
import humps from 'humps';
import qs from 'qs';
import TokenManager from '../utils/TokenManager';
import StorageManager from '../utils/StorageManager';
import { localeHandler } from '../utils';
import { devlog } from '../utils/logger';

//トークンkey値の設定
export const BONJIRI_TOKEN_KEY = 'x-bonjiri-token';
const BONJIRI_LOCALE = 'x-bonjiri-locale';

class ApiError {
  constructor({ data, message, errors, http, authExpired }) {
    this.data = data;
    this.message = message;
    this.errors = errors;
    this.http = {
      message: http.message,
      statusCode: http.statusCode,
      statusText: http.statusText
    };
    this.authExpired = authExpired;
  }
}

export default class ApiClient {
  constructor(
    {
      baseURL = process.env.VUE_APP_API_HOST,
      timeout = 600000,
      headers: optionHeaders = {}
    } = {
      baseURL: process.env.VUE_APP_API_HOST,
      timeout: 600000,
      optionHeaders: {}
    }
  ) {
    this.baseURL = baseURL;
    const headers = {
      'Content-Type': 'application/json',
      ...optionHeaders
    }; //オプション指定ヘッダーとデフォルトのトークンヘッダーをマージ
    this.client = Axios.create({
      timeout: timeout,
      headers: headers,
      paramsSerializer: function(params) {
        devlog('paramsSerilizer:', params);
        return qs.stringify(humps.decamelizeKeys(params));
      }
    });

    /**
     * Snakelize Request
     */
    this.client.interceptors.request.use(request => {
      devlog('Start Request:', request);
      if (request.headers['Content-Type'] === 'application/json') {
        request.data = humps.decamelizeKeys(request.data);
      }
      return Promise.resolve(request);
    });

    /**
     * Camelize Response
     */
    this.client.interceptors.response.use(
      response => {
        devlog('Response: ', response);
        response.data = humps.camelizeKeys(response.data);
        return Promise.resolve(response);
      },
      error => {
        devlog('Error Response:' + error);

        //401権限エラーの場合は、強制的にログイン画面に画面遷移
        //ただし、'/signup/email_confirmed/'の場合は遷移しない（ページ内で401を解決するため）
        if (
          error.response.status === 401 &&
          location.href.indexOf('/signup/email_confirmed/') < 0
        ) {
          TokenManager.remove(); //ローカル側トークンの無効化
          location.href =
            '/cp/' +
            StorageManager.getItem('CAMPAIGN_CODE_SESSION_KEY') +
            '/login';
          return;
        }

        error.response.data = humps.camelizeKeys(error.response.data);
        return Promise.reject(error);
      }
    );

    /**
     * handle data property
     */
    this.client.interceptors.response.use(
      response => {
        return Promise.resolve(response.data);
      },
      error => {
        devlog('Error Response:' + error);
        if (error.response.status === 500) {
          // 500時はAPIのレスポンスが正常でないためハンドリング
          return new Promise((resolve, reject) => {
            reject(
              new ApiError({
                data: null,
                message: error.response.data,
                http: {
                  message: error.response.data,
                  statusCode: error.response.status,
                  statusText: error.response.statusText
                },
                authExpired: false
              })
            );
          });
        } else if (error.response.status === 401) {
          // 500時はAPIのレスポンスが正常でないためハンドリング
          // store.dispatch('initState');
          return new Promise((resolve, reject) => {
            reject(
              new ApiError({
                data: null,
                message: error.response.data,
                http: {
                  message: error.response.data,
                  statusCode: error.response.status,
                  statusText: error.response.statusText
                },
                authExpired: true
              })
            );
          });
        } else {
          return new Promise((resolve, reject) => {
            reject(
              new ApiError({
                data: error.response.data,
                message: error.response.data.result.message,
                errors: error.response.data.result.errors,
                http: {
                  message: error.response.data,
                  statusCode: error.response.status,
                  statusText: error.response.statusText
                },
                authExpired: false
              })
            );
          });
        }
      }
    );
  }

  /**
   * トークンをヘッダーにマージする
   * @param {*} header
   */
  addTokenToHeader(config) {
    const token = TokenManager.get();
    const defaultHeader = {
      [BONJIRI_TOKEN_KEY]: token,
      [BONJIRI_LOCALE]: localeHandler.getLocale()
    };

    if (!config) {
      // なにもない場合はデフォルトの値を返却
      return { headers: defaultHeader };
    } else if (config.headers) {
      // header指定がある場合は指定を優先してマージ
      config.headers = { ...defaultHeader, ...config.headers };
    } else {
      config.headers = defaultHeader;
    }
    return config;
  }

  get(apiPath, config = null) {
    return this.client.get(
      this.baseURL + apiPath,
      this.addTokenToHeader(config)
    );
  }

  post(apiPath, body = null, config = null) {
    return this.client.post(
      this.baseURL + apiPath,
      body,
      this.addTokenToHeader(config)
    );
  }

  patch(apiPath, body = null, config = null) {
    return this.client.patch(
      this.baseURL + apiPath,
      body,
      this.addTokenToHeader(config)
    );
  }
}
